import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { Lang } from "@/content/tracks";

export interface FAQItem {
  question: { en: string; fr: string };
  answer: { en: string; fr: string };
}

interface PillarFAQProps {
  items: FAQItem[];
  lang: Lang;
  sectionHeadline?: { en: string; fr: string };
  sectionEyebrow?: { en: string; fr: string };
  defaultOpenIndex?: number;
}

/**
 * Reusable FAQ component for pillar pages. Each pillar passes its own questions.
 */
export default function PillarFAQ({ items, lang, sectionHeadline, sectionEyebrow, defaultOpenIndex = 0 }: PillarFAQProps) {
  const resolvedLang: "en" | "fr" = lang === "fr" ? "fr" : "en";

  const headline = sectionHeadline ?? {
    en: "Common questions",
    fr: "Questions fréquentes",
  };

  const eyebrow = sectionEyebrow ?? {
    en: "— FAQ",
    fr: "— FAQ",
  };

  return (
    <section className="container py-12 md:py-16">
      <div
        className="font-mono text-xs font-semibold uppercase tracking-[0.2em]"
        style={{ color: "hsl(var(--learn-warning))" }}
      >
        {eyebrow[resolvedLang] ?? eyebrow.en}
      </div>
      <h2
        className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl"
        style={{ color: "hsl(var(--learn-text))" }}
      >
        {headline[resolvedLang] ?? headline.en}
      </h2>

      <Accordion
        type="single"
        collapsible
        className="mt-8 max-w-3xl"
        defaultValue={
          defaultOpenIndex >= 0 && defaultOpenIndex < items.length
            ? `faq-${defaultOpenIndex}`
            : undefined
        }
      >
        {items.map((item, index) => (
          <AccordionItem key={index} value={`faq-${index}`}>
            <AccordionTrigger className="text-left text-base font-semibold">
              {item.question[resolvedLang] ?? item.question.en}
            </AccordionTrigger>
            <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
              {item.answer[resolvedLang] ?? item.answer.en}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}
